import { useTranslate } from '@refinedev/core';
import { Card, Descriptions, Flex, Typography, theme } from 'antd';
import type { DescriptionsProps } from 'antd';
import type {
  PublicationListOut,
  TestimonialOut,
} from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { PromotedTag } from '@/components/feedback/tags';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { formatDateTime } from '@/utils/format';

const { Paragraph, Text } = Typography;

interface TestimonialGeneralTabProps {
  record: TestimonialOut;
  publication?: PublicationListOut;
}

export const TestimonialGeneralTab = ({
  record,
  publication,
}: TestimonialGeneralTabProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const verticalDescProps = useVerticalDescProps();

  const basicInfoItems: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: <Text code>{record.id}</Text>,
    },
    {
      key: 'publication',
      label: t('feedback.testimonials.fields.publication_id'),
      children: orDash(
        record.publication_id ? (
          <PublicationInfoPopover
            publication={publication}
            publicationId={record.publication_id}
          />
        ) : null
      ),
    },
    {
      key: 'author_name',
      label: t('feedback.testimonials.fields.author_name'),
      children: orDash(record.author_name),
    },
    {
      key: 'is_promoted',
      label: t('feedback.testimonials.fields.is_promoted'),
      children: <PromotedTag value={record.is_promoted} />,
    },
    {
      key: 'comment',
      label: t('feedback.testimonials.fields.comment'),
      span: 3,
      children: orDash(
        record.comment ? (
          <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
            {record.comment}
          </Paragraph>
        ) : null
      ),
    },
  ];

  const systemInfoItems: DescriptionsProps['items'] = [
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  return (
    <Flex vertical gap={token.margin}>
      <Card title={t('base.sections.basic_info')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, md: 3 }}
          items={basicInfoItems}
        />
      </Card>
      <Card title={t('base.sections.system_info')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, md: 3 }}
          items={systemInfoItems}
        />
      </Card>
    </Flex>
  );
};
